import React, { useRef, MutableRefObject, useContext, useCallback, useState } from 'react';
import {
  IPropertyChild,
  getPropertyDisplayLabel,
  getKeyFromPath,
} from 'components/property-editors/utils/ProviderNodeUtils';
import { HasOpenState } from 'hooks/HasOpenState';
import './MappingTypeSelector.less';
import {
  getMappingEditorValues,
  getEditorConfigValue,
  getEditorConfigDisplayName,
  isMappingUnknown,
} from 'components/property-editors/utils/EditorConfigurationUtils';
import { Callout } from 'components/layout/Callout';
import { ActionLinks } from 'components/ActionLinks/ActionLinks';
import { UnknownMappingField } from 'components/inputs/UnknownMappingField';
import { getParentValidationIssues, getParentValidationSummary } from 'utils/ValidationUtils';
import { PropertyEditorContext } from 'components/property-editors/contexts/PropertyEditorContext';
import { FieldTypeDisplay } from 'components/inputs/FieldTypeDisplay';
import { Label } from 'components/inputs/Label';
import { Editor } from 'components/Editors/Editor';
import { EditorChildren } from '../PropertyGrid/EditorChildren';
import { InputMappingConstants, OutputMappingConstants } from 'components/property-editors/MappingConstants';
import {
  getMappingField,
  getMappingFieldPath,
  getChildPath,
  getMappingName,
  getPickPath,
  isBuildJoinArrayMapping,
} from './helpers/MappingSelectorHelpers';
import { ListMappingSelector } from './ListMappingSelector';
import { getDataPath } from 'components/Editors/utils/EditorDataAttributeHelpers';
import { PickButton } from 'components/buttons/PickButton/PickButton';
import { ComputeDateMapping } from './ComputeDateMapping';

interface IMappingTypeSelectorProps extends React.HTMLProps<{}> {
  child: IPropertyChild;
  childId?: string;
  path: string[];
}

/**
 * Mapping Type Selector component.
 *
 * Copyright (c) 2019 Decisions All Rights Reserved
 */
export function MappingTypeSelector({ child, childId, path, children }: IMappingTypeSelectorProps) {
  const { isOpen, toggleOpen } = HasOpenState();
  const linkRef: MutableRefObject<HTMLButtonElement | null> = useRef(null);
  const currentMapping = getMappingName(child);
  const prevMappingType = useRef(currentMapping);
  const [editorShouldShowDialog, setEditorShouldShowDialog] = useState(false);
  const context = useContext(PropertyEditorContext);
  const values = getMappingEditorValues(getMappingField(child));
  const fieldPath = getMappingFieldPath(child, path);
  const validationIssues = getParentValidationIssues(child);
  const typeDisplayName = getEditorConfigDisplayName(getMappingField(child));
  const validation = getParentValidationSummary(child);
  const childPath = getChildPath(path, child);
  const canAdd = isBuildJoinArrayMapping(currentMapping);
  const isBuildData = currentMapping === InputMappingConstants.MAPPING_BUILD;
  const isInputConverter = currentMapping === InputMappingConstants.MAPPING_RUN_CONVERTER_FLOW;
  const isList = getEditorConfigValue(getMappingField(child), 'isList');
  const isDateMapping = currentMapping === InputMappingConstants.MAPPING_DATETIME_COMPUTE;
  const isOutput =
    currentMapping === OutputMappingConstants.MAPPING_ARRAY_FIRST_ITEM ||
    currentMapping === OutputMappingConstants.MAPPING_ARRAY_LAST_ITEM ||
    currentMapping === OutputMappingConstants.MAPPING_EXPOSE_PROPERTIES;

  const onOptionSelect = useCallback(
    async (value: string) => {
      toggleOpen();
      setEditorShouldShowDialog(value !== prevMappingType.current && value === InputMappingConstants.MAPPING_SELECT);
      prevMappingType.current = value;
      await context.onChange(value, fieldPath);
    },
    [context, fieldPath, toggleOpen]
  );

  /**
   * Handle UnknownMappingField "Pick" click. This needs to live here because this call-back's first move (setting the mapping
   * to Select From Flow), will cause the UnknownMappingField itself to be unmounted.
   */
  const onUnknownPickButtonClick = useCallback(async () => {
    await context.onChange('Select From Flow', fieldPath);
    await context.onShowDialogue({
      node: child as IPropertyChild,
      name: 'UnknownMappingField',
      editingContextId: context.editingContextId,
      path: getPickPath(path),
      onDone: context.refreshContext,
      options: {
        mappingContext: context.mappingContext,
      },
    });
  }, [child, context, fieldPath, path]);

  return (
    <Label
      validation={validation}
      label={child.Node.Name}
      htmlFor={childId || child.Node.Name}
      dataLabel={getPropertyDisplayLabel(child)}
      dataPath={getDataPath(path)}
      actions={<PickButton onClick={toggleOpen} text={currentMapping} buttonRef={linkRef} />}
      doNotWrap
    >
      {/* FieldTypeDisplay is only conditional in extant app, wut? */}
      {/* still prefer FieldTypeDisplay under the field, because in between field and label feels disjointed,
      but we'd need an outlet to put it beside or under the buttons. Under the buttons is _super_ disjointed */}
      {<FieldTypeDisplay entityTypeName={typeDisplayName} isList={isList} />}
      {child?.Children && isMappingUnknown(child.Children[0]) && (
        <UnknownMappingField
          child={child}
          path={childPath}
          validationIssues={validationIssues}
          onPickClick={onUnknownPickButtonClick}
          isList={isList}
        />
      )}
      {canAdd && <ListMappingSelector child={child} path={path} fieldPath={fieldPath} mapping={currentMapping} />}
      {!canAdd && !isBuildData && !isOutput && child.Children[1] && !isInputConverter && (
        <>
          <Editor
            key={getKeyFromPath(path, child)}
            child={child.Children[1] as IPropertyChild}
            path={childPath}
            showDialogOnRender={editorShouldShowDialog}
            hideInputLabel
          />
          {/* Output ConverterFlow Mapping has mapping for Flow outputs */}
          {!!child.Children[OutputMappingConstants.MAPPING_CONVERTER_FLOW__MAPPING_INDEX] && !isDateMapping && (
            <EditorChildren nodes={[child.Children[2]]} path={path} />
          )}
        </>
      )}
      {/* ComputeDateMapping required 4 children and while deleting the nodes we are deleting it one by one hence adding check here. */}
      {isDateMapping && child.Children.length === 4 && <ComputeDateMapping node={child} path={path} />}
      {(isBuildData || isInputConverter || isOutput) && (
        <section className="dpc-mapping-selector__build-data">
          <EditorChildren nodes={child.Children.slice(1)} path={path} />
        </section>
      )}
      {children}
      <Callout
        isOpen={isOpen}
        onClose={toggleOpen}
        sourceRef={linkRef}
        placement="left"
        showArrow={true}
        flipBehavior="counterclockwise"
      >
        <ActionLinks values={values} callback={onOptionSelect} />
      </Callout>
    </Label>
  );
}
